import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Grid, Button, Chip, Divider,
  Card, CardContent, CircularProgress, Alert, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, Tooltip, Tabs, Tab, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MedicalServices as MedicalServicesIcon,
  AttachMoney as AttachMoneyIcon,
  AccessTime as AccessTimeIcon,
  Notes as NotesIcon,
  Inventory as InventoryIcon,
  LocalHospital as LocalHospitalIcon,
  Assignment as AssignmentIcon,
  Healing as HealingIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

// Get API URL from environment variables
import { API_URL } from '../../utils/apiConfig';
import { formatCurrencySync, getOrganizationTaxSettings } from '../../utils/currencyUtils';
import { downloadTreatmentPDF, openTreatmentPDF } from '../../utils/pdfTreatment';
import {
  SESSION_EXPIRED_MESSAGE,
  formatFailedActionMessage,
  formatSuccessActionMessage,
} from '../../utils/uiMessages';
import { getApiErrorMessage } from '../../utils/errorUtils';

const TreatmentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const fetchTreatmentDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // Make API call to get treatment details
      const response = await axios.get(`${API_URL}/treatments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTreatment(response.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching treatment details:', err);
      setError(formatFailedActionMessage('load', 'treatment details'));
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTreatmentDetails();
  }, [fetchTreatmentDetails]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEdit = () => {
    navigate(`/treatments/${id}/edit`);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(SESSION_EXPIRED_MESSAGE);
        return;
      }

      await axios.delete(`${API_URL}/treatments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      toast.success(formatSuccessActionMessage('deleted', 'Treatment'));
      setDeleteDialogOpen(false);
      navigate('/treatments');
    } catch (err) {
      console.error('Error deleting treatment:', err);
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('delete', 'treatment')));
      setDeleteDialogOpen(false);
    }
  };

  useEffect(() => {
    fetchTreatmentDetails();
  }, [fetchTreatmentDetails]);

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle print treatment PDF
  const handlePrint = async () => {
    try {
      if (!treatment) return;
      
      // Fetch full treatment data with populated patient if not already populated
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(SESSION_EXPIRED_MESSAGE);
        return;
      }

      const response = await axios.get(`${API_URL}/treatments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await openTreatmentPDF(response.data.data);
    } catch (error) {
      console.error('Error printing treatment:', error);
      toast.error(formatFailedActionMessage('print', 'treatment'));
    }
  };

  // Handle download treatment PDF
  const handleDownload = async () => {
    try {
      if (!treatment) return;
      
      // Fetch full treatment data with populated patient if not already populated
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(SESSION_EXPIRED_MESSAGE);
        return;
      }

      const response = await axios.get(`${API_URL}/treatments/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await downloadTreatmentPDF(response.data.data);
      toast.success(formatSuccessActionMessage('downloaded', 'Treatment PDF'));
    } catch (error) {
      console.error('Error downloading treatment:', error);
      toast.error(formatFailedActionMessage('download', 'treatment'));
    }
  };

  // Handle create invoice from treatment — use treatment data first (clinic, patient, items)
  const handleCreateInvoice = async () => {
    try {
      if (!treatment) return;

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(SESSION_EXPIRED_MESSAGE);
        return;
      }

      // Check if treatment has a patient
      if (!treatment.patient) {
        toast.error('This treatment is not associated with a patient. Cannot create invoice.');
        return;
      }

      // Patient ID from treatment first
      const patientId = treatment.patient._id || treatment.patient;

      // Clinic: use treatment's clinic first, then patient's registered clinic, then first available
      let clinicId = null;
      if (treatment.clinic) {
        clinicId = treatment.clinic._id || treatment.clinic;
      }
      if (!clinicId) {
        const patientResponse = await axios.get(`${API_URL}/patients/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const patient = patientResponse.data.data;
        if (patient.registeredClinic) {
          clinicId = patient.registeredClinic._id || patient.registeredClinic;
        }
      }
      if (!clinicId) {
        const clinicsResponse = await axios.get(`${API_URL}/clinics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const clinics = clinicsResponse.data.data || [];
        if (clinics.length === 0) {
          toast.error('No clinics available. Cannot create invoice.');
          return;
        }
        clinicId = clinics[0]._id;
      }

      if (!clinicId) {
        toast.error('Unable to determine clinic. Cannot create invoice.');
        return;
      }

      // Use organization tax settings (rate as %, inclusive flag)
      const taxSettings = await getOrganizationTaxSettings();
      const taxRateDecimal = taxSettings.taxRate; // e.g. 0.18
      const taxRatePct = Math.round(taxRateDecimal * 100);

      const quantity = treatment.teeth && Array.isArray(treatment.teeth) && treatment.teeth.length > 0
        ? treatment.teeth.length
        : 1;
      const unitPrice = treatment.price ?? treatment.cost ?? 0;
      const discount = 0;
      const itemSubtotal = quantity * unitPrice;
      const itemDiscount = itemSubtotal * (discount / 100);
      const discountedAmount = itemSubtotal - itemDiscount;

      let itemAmount, subtotal, taxAmount, totalAmount;
      if (taxSettings.taxInclusive) {
        // Price on card is including tax: item amount = full price; PDF will derive net and tax
        itemAmount = discountedAmount;
        totalAmount = discountedAmount;
        subtotal = Math.round(totalAmount / (1 + taxRateDecimal));
        taxAmount = totalAmount - subtotal;
      } else {
        // Price on card is pre-tax: item amount = pre-tax; PDF will add tax on top
        itemAmount = discountedAmount;
        subtotal = discountedAmount;
        taxAmount = Math.round(subtotal * taxRateDecimal);
        totalAmount = subtotal + taxAmount;
      }

      const invoiceDate = new Date();
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + 30); // 30 days from invoice date

      const invoiceData = {
        patient: patientId,
        clinic: clinicId,
        treatment: treatment._id,
        invoiceDate: invoiceDate.toISOString(),
        dueDate: dueDate.toISOString(),
        taxInclusive: taxSettings.taxInclusive,
        items: [{
          description: treatment.name || treatment.description || 'Treatment',
          quantity: quantity,
          unitPrice: unitPrice,
          discount: discount,
          tax: taxRatePct,
          amount: itemAmount
        }],
        subtotal,
        discountAmount: itemDiscount,
        taxAmount,
        totalAmount,
        notes: treatment.notes || '',
        termsAndConditions: 'Payment is due within 30 days. Late payments are subject to a 2% monthly interest charge.',
        isGstInvoice: true,
        gstDetails: {
          gstNumber: 'GST123456789',
          cgst: 9,
          sgst: 9,
          igst: 0,
          hsnCode: '998313'
        }
      };

      // Create invoice
      const invoiceResponse = await axios.post(`${API_URL}/billing`, invoiceData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(formatSuccessActionMessage('created', 'Invoice'));
      
      // Navigate to the created invoice
      setTimeout(() => {
        navigate(`/billing/invoice/${invoiceResponse.data.data._id}`);
      }, 1000);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(getApiErrorMessage(error, formatFailedActionMessage('create', 'invoice')));
    }
  };

  // Format price as currency using organization settings
  const formatPrice = (price) => {
    return formatCurrencySync(price);
  };

  // Format date
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMM yyyy, h:mm a');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to Treatments
          </Button>
        </Box>
      </Container>
    );
  }

  if (!treatment) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Treatment not found</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back to Treatments
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with treatment name and actions */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Typography variant="h5" component="h1">
              {treatment.name}
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Print Treatment">
              <IconButton color="primary" onClick={handlePrint} sx={{ mr: 1 }}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton color="primary" onClick={handleDownload} sx={{ mr: 1 }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {treatment?.patient && (
              <Tooltip title="Create Invoice">
                <IconButton color="primary" onClick={handleCreateInvoice} sx={{ mr: 1 }}>
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit Treatment">
              <IconButton color="primary" onClick={handleEdit} sx={{ mr: 1 }}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Treatment">
              <IconButton color="error" onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Treatment Code
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {treatment.code}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Category
            </Typography>
            <Chip 
              label={treatment.category} 
              color="primary" 
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Price
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, color: 'success.main' }} />
              {formatPrice(treatment.price)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Duration
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'info.main' }} />
              {treatment.duration} minutes
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different sections */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Procedure" />
          <Tab label="Medical Info" />
          <Tab label="Related Treatments" />
          <Tab label="Statistics" />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ mb: 4 }}>
        {/* Overview Tab */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Treatment Image, Teeth and Description */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Treatment Overview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {/* Selected teeth summary */}
                {treatment.teeth && Array.isArray(treatment.teeth) && treatment.teeth.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Teeth involved
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {treatment.teeth
                        .slice()
                        .sort((a, b) => a - b)
                        .map((tooth) => (
                          <Chip
                            key={tooth}
                            label={tooth}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                    </Box>
                  </Box>
                )}
                {treatment.imageUrl && (
                  <Box sx={{ mb: 2 }}>
                    <img 
                      src={treatment.imageUrl} 
                      alt={treatment.name} 
                      style={{ width: '100%', borderRadius: 8, maxHeight: 300, objectFit: 'cover' }} 
                    />
                  </Box>
                )}
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ whiteSpace: 'pre-line' }}
                >
                  {treatment.description}
                </Typography>
                {treatment.notes && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
                    <NotesIcon color="action" sx={{ mr: 1, mt: 0.5 }} />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      <strong>Notes:</strong>{' '}
                      {treatment.notes}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Equipment and Details */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Required Equipment
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List>
                  {treatment.requiredEquipment && treatment.requiredEquipment.length > 0 ? (
                    treatment.requiredEquipment.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <InventoryIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No equipment specified" secondary="No special equipment required for this treatment" />
                    </ListItem>
                  )}
                </List>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Additional Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Status
                      </Typography>
                      <Chip 
                        label={treatment.isActive ? 'Active' : 'Inactive'} 
                        color={treatment.isActive ? 'success' : 'default'} 
                        size="small" 
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    {/* Created / Last Updated intentionally omitted from UI */}
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Procedure Tab */}
        {tabValue === 1 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Procedure Steps
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {treatment.procedureSteps && treatment.procedureSteps.length > 0 ? (
                treatment.procedureSteps.map((step, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Chip label={index + 1} color="primary" size="small" />
                    </ListItemIcon>
                    <ListItemText primary={step} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No procedure steps specified" />
                </ListItem>
              )}
            </List>
          </Paper>
        )}

        {/* Medical Info Tab */}
        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contraindications
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {treatment.contraindications && treatment.contraindications.length > 0 ? (
                    treatment.contraindications.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <LocalHospitalIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No contraindications specified" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Side Effects
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {treatment.sideEffects && treatment.sideEffects.length > 0 ? (
                    treatment.sideEffects.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <AssignmentIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No side effects specified" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Aftercare
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {treatment.aftercare && treatment.aftercare.length > 0 ? (
                    treatment.aftercare.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <HealingIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No aftercare instructions specified" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Related Treatments Tab */}
        {tabValue === 3 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Related Treatments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {treatment.relatedTreatments && treatment.relatedTreatments.length > 0 ? (
                treatment.relatedTreatments.map((relatedTreatment) => (
                  <Grid item xs={12} sm={6} md={4} key={relatedTreatment.id || relatedTreatment._id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MedicalServicesIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            {relatedTreatment.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Code: {relatedTreatment.code}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => navigate(`/treatments/${relatedTreatment._id || relatedTreatment.id}`)}
                          >
                            View Details
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" align="center">
                    No related treatments specified
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Statistics Tab */}
        {tabValue === 4 && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Usage Statistics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: 'primary.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3" align="center">
                      {treatment.usageStatistics?.totalPerformed || 0}
                    </Typography>
                    <Typography variant="body2" align="center">
                      Total Performed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: 'info.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3" align="center">
                      {treatment.usageStatistics?.averageDuration || 0}
                    </Typography>
                    <Typography variant="body2" align="center">
                      Avg. Duration (mins)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: 'success.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h3" align="center">
                      {treatment.usageStatistics?.successRate || 0}%
                    </Typography>
                    <Typography variant="body2" align="center">
                      Success Rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 2, bgcolor: 'secondary.light', color: 'white' }}>
                  <CardContent>
                    <Typography variant="h5" align="center">
                      {treatment.usageStatistics?.commonlyUsedWith && treatment.usageStatistics.commonlyUsedWith.length > 0
                        ? treatment.usageStatistics.commonlyUsedWith.join(', ')
                        : 'N/A'}
                    </Typography>
                    <Typography variant="body2" align="center">
                      Commonly Used With
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Treatment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the treatment "{treatment.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TreatmentDetails;